import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useQueryClient } from 'react-query';
import { useHistory, useLocation } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import {
  FiPlus,
  FiRefreshCw,
  FiXCircle,
  FiFilter,
  FiMail,
  FiSend,
  FiClock,
  FiCheckCircle,
} from 'react-icons/fi';
import firebase from '@/config/firebase.config';
import { useActiveCompanyRole, useIsAdmin } from '@/hooks/usePermission';
import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import { TabBar, tabBarVariants } from '@/components/TabBar';
import { EmptyState } from '@/components/EmptyState';
import { Button, buttonVariants, buttonSizes } from '@/components/Button';
import { Dropdown, dropdownSizes } from '@/components/Dropdown';
import { Table } from '@/components/Table';
import { Pagination } from '@/components/Pagination';
import { StatusBadge, statusBadgeTones } from '@/components/StatusBadge';
import { InitialsBadge } from '@/components/InitialsBadge';
import { ConfirmActionDialog } from '@/components/ConfirmActionDialog';
import DefaultAvatar from '@assets/img/svg/avatar.svg';
import { appRoutes } from '@/routes';
import { mergeEmployeePageFiltersIntoSearch } from '@/utils/employeePageUrlFilters';
import styles from './InvitationsHub.module.scss';
import { fetchManagedTeamIdsForCompany } from '@/services/userMembership';
import {
  acceptInviteFromInvites,
  declineInviteFromInvites,
  revokeInvite,
  resendInvite,
} from '@/services/invitesClient';
import { addNotification, notificationStates } from '@/components/Notification';
import { authActionTypes } from '@/store/Auth/Auth.actionTypes';
import { queryKeys } from '@/config/queryKeys';
import { teamActionTypes } from '@/store/Team/Team.actionTypes';

const database = firebase.firestore();

const TAB_MY = 'myInvites';
const TAB_SENT = 'sentInvites';
const PAGE_SIZE = 10;

function formatShowingRange(page, total) {
  if (total === 0) return null;
  const start = (page - 1) * PAGE_SIZE + 1;
  const end = Math.min(page * PAGE_SIZE, total);
  return `Showing ${start} to ${end} of ${total} invite${total === 1 ? '' : 's'}`;
}

const STATUS_CONFIG = {
  pending: {
    label: 'Pending',
    tone: statusBadgeTones.warning,
    icon: <FiClock />,
  },
  accepted: {
    label: 'Accepted',
    tone: statusBadgeTones.success,
    icon: <FiCheckCircle />,
  },
  revoked: {
    label: 'Revoked',
    tone: statusBadgeTones.error,
    icon: <FiXCircle />,
  },
};

const STATUS_FILTER_OPTIONS = [
  { value: null, label: 'All Status' },
  { value: 'pending', label: 'Pending' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'revoked', label: 'Revoked' },
];

// Plain single-line text cell (Department, Role, dash fallbacks) — every
// cell must go through Text so its size/weight matches the rest of the row
// instead of inheriting the page's raw default, which reads as a different
// font next to the deliberately-styled cells around it.
function PlainCell({ children }) {
  return (
    <Text variant={textVariantsEnum.Text_sm} fontWeight={fontWeightEnum.Normal}>
      {children}
    </Text>
  );
}

function StatusCell({ status }) {
  const config = STATUS_CONFIG[status] || {
    label: status || '—',
    tone: statusBadgeTones.default,
  };
  return (
    <StatusBadge label={config.label} tone={config.tone} icon={config.icon} />
  );
}

function toJsDate(value) {
  if (value == null) return null;
  if (typeof value.toDate === 'function') {
    try {
      return value.toDate();
    } catch {
      return null;
    }
  }
  if (typeof value === 'object' && value.seconds != null) {
    return new Date(value.seconds * 1000);
  }
  return null;
}

function DateSentCell({ value }) {
  const date = toJsDate(value);
  if (!date) return <PlainCell>—</PlainCell>;
  return (
    <div className={styles.dateCell}>
      <Text
        variant={textVariantsEnum.Text_sm}
        fontWeight={fontWeightEnum.Medium}
      >
        {date.toLocaleDateString(undefined, {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })}
      </Text>
      <Text
        variant={textVariantsEnum.Text_xs}
        fontWeight={fontWeightEnum.Normal}
        color="gray-500"
      >
        {date.toLocaleTimeString(undefined, {
          hour: '2-digit',
          minute: '2-digit',
        })}
      </Text>
    </div>
  );
}

function scopeLabel(data) {
  if (data.scopeType === 'company_admin') {
    return 'Company (Admin)';
  }
  return data.teamName || data.contextTeamName || data.teamId || '—';
}

// Person-identity cell shared by Invitee/Sent-By/Invited-By columns — an
// avatar image (when we have one, e.g. the current user), initials (when we
// only have a name/email to hash a color from), or plain text (when we have
// neither, e.g. an external inviter's photo we don't store).
function PersonCell({ name, email, avatarSrc, showInitials = true }) {
  const displayName = name || (email ? email.split('@')[0] : '—');
  return (
    <div className={styles.personCell}>
      {avatarSrc ? (
        <img src={avatarSrc} alt="" className={styles.personAvatarImg} />
      ) : showInitials ? (
        <InitialsBadge label={displayName} twoLetter />
      ) : null}
      <div className={styles.personCellText}>
        <Text
          variant={textVariantsEnum.Text_sm}
          fontWeight={fontWeightEnum.Medium}
        >
          {displayName}
        </Text>
        {email && (
          <Text
            variant={textVariantsEnum.Text_xs}
            fontWeight={fontWeightEnum.Normal}
            color="gray-500"
          >
            {email}
          </Text>
        )}
      </div>
    </div>
  );
}

function CompanyTeamCell({ companyName, teamLabel }) {
  return (
    <div className={styles.personCell}>
      <InitialsBadge label={companyName || '?'} shape="square" />
      <div className={styles.personCellText}>
        <Text
          variant={textVariantsEnum.Text_sm}
          fontWeight={fontWeightEnum.Medium}
        >
          {companyName || '—'}
        </Text>
        {teamLabel && teamLabel !== '—' && (
          <Text
            variant={textVariantsEnum.Text_xs}
            fontWeight={fontWeightEnum.Normal}
            color="gray-500"
          >
            Team: {teamLabel}
          </Text>
        )}
      </div>
    </div>
  );
}

export const InvitationsHubComponent = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const history = useHistory();
  const location = useLocation();
  const userProfile = useSelector((state) => state.auth.profile);
  const companyId = userProfile?.activeCompany?.companyId;
  const role = useActiveCompanyRole();
  const isAdmin = useIsAdmin();

  const [activeTab, setActiveTab] = useState(TAB_MY);
  const [myInvites, setMyInvites] = useState([]);
  const [sentInvites, setSentInvites] = useState([]);
  const [loadingMy, setLoadingMy] = useState(true);
  const [loadingSent, setLoadingSent] = useState(true);
  const [managedTeamIds, setManagedTeamIds] = useState([]);
  const [managedTeamsReady, setManagedTeamsReady] = useState(
    () => role !== 'Manager'
  );
  const [actionLoadingIds, setActionLoadingIds] = useState(new Set());
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [statusFilter, setStatusFilter] = useState(STATUS_FILTER_OPTIONS[0]);
  const [myPage, setMyPage] = useState(1);
  const [sentPage, setSentPage] = useState(1);

  const addLoading = (id) =>
    setActionLoadingIds((prev) => new Set([...prev, id]));
  const removeLoading = (id) =>
    setActionLoadingIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });

  const showSentTab = !!companyId && (isAdmin || role === 'Manager');
  const userEmail = useMemo(() => {
    const u = firebase.auth().currentUser;
    const fromAuth = u?.email?.toLowerCase().trim();
    if (fromAuth) return fromAuth;
    return userProfile?.email?.toLowerCase().trim() || null;
  }, [userProfile?.email]);

  useEffect(() => {
    if (!userProfile?.id || !companyId || role !== 'Manager') {
      setManagedTeamIds([]);
      setManagedTeamsReady(true);
      return;
    }
    setManagedTeamsReady(false);
    let cancelled = false;
    fetchManagedTeamIdsForCompany(userProfile.id, companyId).then((res) => {
      if (cancelled) return;
      setManagedTeamIds(res.success ? res.data || [] : []);
      setManagedTeamsReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [userProfile?.id, companyId, role]);

  useEffect(() => {
    if (!userEmail) {
      setMyInvites([]);
      setLoadingMy(false);
      return;
    }
    setLoadingMy(true);
    const q = database
      .collection('invites')
      .where('inviteeEmail', '==', userEmail)
      .orderBy('createdAt', 'desc')
      .limit(100);

    const unsub = q.onSnapshot(
      (snap) => {
        setMyInvites(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
        setLoadingMy(false);
      },
      (err) => {
        console.error('[InvitationsHub] my invites', err);
        addNotification({
          message: err.message || 'Could not load your invitations.',
          level: notificationStates.error,
        });
        setMyInvites([]);
        setLoadingMy(false);
      }
    );
    return () => unsub();
  }, [userEmail]);

  useEffect(() => {
    if (!companyId || !showSentTab) {
      setSentInvites([]);
      setLoadingSent(false);
      return;
    }
    if (role === 'Manager' && !managedTeamsReady) {
      setLoadingSent(true);
      return;
    }
    setLoadingSent(true);
    const q = database
      .collection('invites')
      .where('companyId', '==', companyId)
      .orderBy('createdAt', 'desc')
      .limit(200);

    const unsub = q.onSnapshot(
      (snap) => {
        let rows = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        if (role === 'Manager') {
          rows = rows.filter(
            (r) =>
              r.scopeType === 'team' &&
              r.teamId &&
              managedTeamIds.includes(r.teamId)
          );
        }
        setSentInvites(rows);
        setLoadingSent(false);
      },
      (err) => {
        console.error('[InvitationsHub] sent invites', err);
        addNotification({
          message: err.message || 'Could not load sent invitations.',
          level: notificationStates.error,
        });
        setSentInvites([]);
        setLoadingSent(false);
      }
    );
    return () => unsub();
  }, [companyId, showSentTab, role, managedTeamIds, managedTeamsReady]);

  const tabOptions = useMemo(() => {
    const base = [
      {
        label: 'My Invitations',
        value: TAB_MY,
        count: myInvites.length,
        icon: <FiMail size={16} />,
      },
    ];
    if (showSentTab) {
      base.push({
        label: 'Invites Sent',
        value: TAB_SENT,
        count: sentInvites.length,
        icon: <FiSend size={16} />,
      });
    }
    return base;
  }, [showSentTab, myInvites.length, sentInvites.length]);

  useEffect(() => {
    if (!showSentTab && activeTab === TAB_SENT) {
      setActiveTab(TAB_MY);
    }
  }, [showSentTab, activeTab]);

  const filteredSentInvites = useMemo(
    () =>
      statusFilter?.value
        ? sentInvites.filter((row) => row.status === statusFilter.value)
        : sentInvites,
    [sentInvites, statusFilter]
  );

  useEffect(() => {
    setSentPage(1);
  }, [statusFilter]);

  const myTotalPages = Math.max(1, Math.ceil(myInvites.length / PAGE_SIZE));
  const pagedMyInvites = myInvites.slice(
    (myPage - 1) * PAGE_SIZE,
    myPage * PAGE_SIZE
  );

  const sentTotalPages = Math.max(
    1,
    Math.ceil(filteredSentInvites.length / PAGE_SIZE)
  );
  const pagedSentInvites = filteredSentInvites.slice(
    (sentPage - 1) * PAGE_SIZE,
    sentPage * PAGE_SIZE
  );

  const handleAcceptInvite = async (inviteId) => {
    const user = firebase.auth().currentUser;
    if (!user) {
      addNotification({
        message: 'Please sign in again.',
        level: notificationStates.error,
      });
      return;
    }
    addLoading(inviteId);
    try {
      const res = await acceptInviteFromInvites(inviteId, null, user);
      if (res.success) {
        if (!userProfile?.activeCompany?.companyId && res.companyId) {
          localStorage.setItem('cid', res.companyId);
        }
        dispatch({ type: authActionTypes.LOAD_CURRENT_ACCOUNT });
        if (res.teamPayload) {
          dispatch({
            type: teamActionTypes.SET_SELECT_TEAM_ID,
            payload: res.teamPayload,
          });
        }
        if (userProfile?.id) {
          queryClient.invalidateQueries(queryKeys.users.detail(userProfile.id));
        }
        addNotification({
          message: 'Invitation accepted.',
          level: notificationStates.success,
        });
      } else {
        addNotification({
          message: res.message || 'Could not accept.',
          level: notificationStates.error,
        });
      }
    } catch (e) {
      addNotification({
        message: e?.message || 'Could not accept.',
        level: notificationStates.error,
      });
    } finally {
      removeLoading(inviteId);
    }
  };

  const handleDeclineInvite = async (inviteId) => {
    addLoading(inviteId);
    try {
      const res = await declineInviteFromInvites(inviteId, null);
      addNotification(
        res.success
          ? { message: 'Invitation declined.', level: notificationStates.info }
          : {
              message: res.message || 'Could not decline.',
              level: notificationStates.error,
            }
      );
    } catch (e) {
      addNotification({
        message: e?.message || 'Could not decline.',
        level: notificationStates.error,
      });
    } finally {
      removeLoading(inviteId);
    }
  };

  const handleResendInvite = async (inviteId) => {
    addLoading(inviteId);
    try {
      const res = await resendInvite(inviteId);
      addNotification(
        res.success
          ? {
              message: 'Invitation resent successfully.',
              level: notificationStates.success,
            }
          : {
              message: res.message || 'Could not resend.',
              level: notificationStates.error,
            }
      );
    } catch (e) {
      addNotification({
        message: e?.message || 'Could not resend.',
        level: notificationStates.error,
      });
    } finally {
      removeLoading(inviteId);
    }
  };

  const handleConfirmRevoke = async () => {
    if (!revokeTarget) return;
    const inviteId = revokeTarget.id;
    addLoading(inviteId);
    try {
      const res = await revokeInvite(inviteId);
      if (res.success) {
        addNotification({
          message: 'Invitation revoked.',
          level: notificationStates.success,
        });
        setRevokeTarget(null);
      } else {
        addNotification({
          message: res.message || 'Could not revoke.',
          level: notificationStates.error,
        });
      }
    } catch (e) {
      addNotification({
        message: e?.message || 'Could not revoke.',
        level: notificationStates.error,
      });
    } finally {
      removeLoading(inviteId);
    }
  };

  const goToSendInvitation = () => {
    history.push({
      pathname: appRoutes().addEmployee,
      search: mergeEmployeePageFiltersIntoSearch(location.search, {
        companyId: companyId ?? '',
        teamId: '',
      }),
    });
  };

  const myInvitesColumns = [
    {
      id: 'status',
      name: 'Status',
      cellRenderer: (row) => <StatusCell status={row.status} />,
    },
    {
      id: 'company',
      name: 'Company / Team',
      cellRenderer: (row) => (
        <CompanyTeamCell
          companyName={row.companyName}
          teamLabel={scopeLabel(row)}
        />
      ),
    },
    {
      id: 'department',
      name: 'Department',
      cellRenderer: (row) => <PlainCell>{scopeLabel(row)}</PlainCell>,
    },
    {
      id: 'role',
      name: 'Role',
      cellRenderer: (row) => <PlainCell>{row.invitedAs || '—'}</PlainCell>,
    },
    {
      id: 'invitedBy',
      name: 'Invited By',
      cellRenderer: (row) => (
        <PersonCell
          name={row.invitedByName}
          email={row.invitedByEmail}
          showInitials={false}
        />
      ),
    },
    {
      id: 'dateSent',
      name: 'Date Sent',
      cellRenderer: (row) => <DateSentCell value={row.createdAt} />,
    },
    {
      id: 'actions',
      name: 'Actions',
      cellRenderer: (row) =>
        row.status === 'pending' ? (
          <div className={styles.actionsCell}>
            <Button
              text="Decline"
              variant={buttonVariants.outline}
              disabled={actionLoadingIds.has(row.id)}
              onClick={() => handleDeclineInvite(row.id)}
            />
            <Button
              text="Accept"
              variant={buttonVariants.primary}
              loading={actionLoadingIds.has(row.id)}
              disabled={actionLoadingIds.has(row.id)}
              onClick={() => handleAcceptInvite(row.id)}
            />
          </div>
        ) : (
          <PlainCell>—</PlainCell>
        ),
    },
  ];

  const sentInvitesColumns = [
    {
      id: 'status',
      name: 'Status',
      cellRenderer: (row) => <StatusCell status={row.status} />,
    },
    {
      id: 'invitee',
      name: 'Invitee',
      cellRenderer: (row) => (
        <PersonCell name={row.inviteeName} email={row.inviteeEmail} />
      ),
    },
    {
      id: 'department',
      name: 'Department',
      cellRenderer: (row) => <PlainCell>{scopeLabel(row)}</PlainCell>,
    },
    {
      id: 'role',
      name: 'Role',
      cellRenderer: (row) => <PlainCell>{row.invitedAs || '—'}</PlainCell>,
    },
    {
      id: 'sentBy',
      name: 'Sent By',
      cellRenderer: () => (
        <PersonCell
          name={userProfile?.name}
          email={userProfile?.email}
          avatarSrc={
            !isEmpty(userProfile?.avatar) ? userProfile.avatar : DefaultAvatar
          }
        />
      ),
    },
    {
      id: 'dateSent',
      name: 'Date Sent',
      cellRenderer: (row) => <DateSentCell value={row.createdAt} />,
    },
    {
      id: 'actions',
      name: 'Actions',
      cellRenderer: (row) =>
        row.status === 'pending' ? (
          <div className={styles.actionsCell}>
            <Button
              text="Resend"
              variant={buttonVariants.outline}
              icon={<FiRefreshCw size={14} />}
              loading={actionLoadingIds.has(row.id)}
              disabled={actionLoadingIds.has(row.id)}
              onClick={() => handleResendInvite(row.id)}
            />
            <Button
              text="Revoke"
              variant={buttonVariants.outline}
              icon={<FiXCircle size={14} />}
              iconColor="var(--error-600)"
              style={{ color: 'var(--error-600)' }}
              disabled={actionLoadingIds.has(row.id)}
              onClick={() =>
                setRevokeTarget({ id: row.id, inviteeLabel: row.inviteeEmail })
              }
            />
          </div>
        ) : (
          <PlainCell>—</PlainCell>
        ),
    },
  ];

  return (
    <div className={styles.invitationsHubPage}>
      <div className={styles.panel}>
        <div className={styles.tabBarWrap}>
          <TabBar
            tabs={tabOptions}
            activeTab={activeTab}
            onTabClick={setActiveTab}
            variant={tabBarVariants.underline}
          />
        </div>

        {activeTab === TAB_MY && (
          <div className={styles.tableSection}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionHeaderText}>
                <Text
                  variant={textVariantsEnum.Text_lg}
                  fontWeight={fontWeightEnum.Semibold}
                  color="gray-900"
                >
                  Invitations you've received
                </Text>
                <Text
                  variant={textVariantsEnum.Text_sm}
                  fontWeight={fontWeightEnum.Normal}
                  color="gray-600"
                >
                  Teams or companies that have invited you.
                </Text>
              </div>
            </div>

            {!loadingMy && myInvites.length === 0 ? (
              <EmptyState
                illustration
                title="No Invitations Yet"
                description="You don't have any pending invitations right now."
              />
            ) : (
              <>
                <Table
                  className={styles.invitationsTable}
                  data={pagedMyInvites}
                  columns={myInvitesColumns}
                />
                <div className={styles.tableFooter}>
                  <Text
                    variant={textVariantsEnum.Text_sm}
                    fontWeight={fontWeightEnum.Normal}
                    color="gray-500"
                  >
                    {formatShowingRange(myPage, myInvites.length)}
                  </Text>
                  <Pagination
                    currentPage={myPage}
                    totalPages={myTotalPages}
                    onPageChange={setMyPage}
                  />
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === TAB_SENT && showSentTab && (
          <div className={styles.tableSection}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionHeaderText}>
                <Text
                  variant={textVariantsEnum.Text_lg}
                  fontWeight={fontWeightEnum.Semibold}
                  color="gray-900"
                >
                  Invitations you've sent
                </Text>
                <Text
                  variant={textVariantsEnum.Text_sm}
                  fontWeight={fontWeightEnum.Normal}
                  color="gray-600"
                >
                  Track and manage invitations you've sent to others.
                </Text>
              </div>
              <div className={styles.sectionHeaderActions}>
                <Dropdown
                  options={STATUS_FILTER_OPTIONS}
                  value={statusFilter}
                  onChange={setStatusFilter}
                  leftIcon={<FiFilter size={14} />}
                  width="170px"
                  size={dropdownSizes.sm}
                  isSearchable={false}
                />
                <Button
                  text="Send Invitation"
                  variant={buttonVariants.primary}
                  size={buttonSizes.lg}
                  icon={<FiPlus size={16} />}
                  onClick={goToSendInvitation}
                />
              </div>
            </div>

            {!loadingSent && filteredSentInvites.length === 0 ? (
              <EmptyState
                illustration
                title="No Invitations Sent"
                description="You haven't sent any invitations for this view yet."
              />
            ) : (
              <>
                <Table
                  className={styles.invitationsTable}
                  data={pagedSentInvites}
                  columns={sentInvitesColumns}
                />
                <div className={styles.tableFooter}>
                  <Text
                    variant={textVariantsEnum.Text_sm}
                    fontWeight={fontWeightEnum.Normal}
                    color="gray-500"
                  >
                    {formatShowingRange(sentPage, filteredSentInvites.length)}
                  </Text>
                  <Pagination
                    currentPage={sentPage}
                    totalPages={sentTotalPages}
                    onPageChange={setSentPage}
                  />
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <ConfirmActionDialog
        visible={!!revokeTarget}
        onHide={() => setRevokeTarget(null)}
        icon={<FiXCircle size={18} color="var(--error-600)" />}
        title="Revoke Invitation"
        message={`Are you sure you want to revoke the invitation for ${revokeTarget?.inviteeLabel}? This cannot be undone.`}
        confirmLabel="Revoke"
        onConfirm={handleConfirmRevoke}
        loading={revokeTarget ? actionLoadingIds.has(revokeTarget.id) : false}
      />
    </div>
  );
};

InvitationsHubComponent.displayName = 'InvitationsHub';
